// Scratch of Fate (docs/04 §17.1). The view reports how much of each cell is revealed; the sim
// decides everything else: prize layout, trap voiding and cash-out value.
// A 4×4 card (Bigger Card) has room for two prize lines; both pay.
import { B } from '../../content/balance';
import { D, type Decimal } from '../decimal';
import type { Rng } from '../rng';
import type { ScratchBonus, ScratchPrize } from '../types';

type Prize = 'p2' | 'p5' | 'p10' | 'p25' | 'jackpot';
const PRIZES: Prize[] = ['p2', 'p5', 'p10', 'p25', 'jackpot'];

export interface ScratchOpts {
  size?: number;
  /** Chance the card has no trap (Clean Card). */
  noTrap?: number;
  /** A Plug part may hide in the card (good path). */
  plug?: boolean;
}

export function createScratch(rng: Rng, par: Decimal, opts: ScratchOpts = {}): ScratchBonus {
  const size = opts.size ?? 3;
  const n = size * size;
  const w = B.SCRATCH_WIN_WEIGHTS;
  const keys = ['none', ...PRIZES] as const;
  const lines = size >= 4 ? 2 : 1;
  const winners: Prize[] = [];
  for (let l = 0; l < lines; l++) {
    const win = keys[rng.weighted(keys.map((k) => (winners.includes(k as Prize) ? 0 : w[k])))] ?? 'none';
    if (win !== 'none') winners.push(win);
  }

  const layout: ScratchPrize[] = [];
  const counts: Record<string, number> = {};
  const put = (p: ScratchPrize) => {
    layout.push(p);
    counts[p] = (counts[p] ?? 0) + 1;
  };
  if (!(opts.noTrap && rng.chance(opts.noTrap))) put('eye');
  if (opts.plug) put('plug');
  for (const win of winners) for (let i = 0; i < 3; i++) put(win);
  // Fill the rest so no other prize reaches 3 (and the winners don't get a 4th).
  while (layout.length < n) {
    const avail = PRIZES.filter((p) => (counts[p] ?? 0) < 2 && !winners.includes(p));
    put(avail.length ? rng.pick(avail) : 'dud');
  }
  rng.shuffle(layout);
  return {
    mode: 'scratch',
    size,
    cells: layout.map((prize) => ({ prize, revealed: 0 })),
    par,
    voided: false,
    done: false,
    payout: D(0),
    plug: opts.plug || undefined,
  };
}

/** Value of the currently revealed, complete matches (0 if none). */
export function scratchValue(card: ScratchBonus): Decimal {
  if (card.voided) return D(0);
  const counts: Partial<Record<Prize, number>> = {};
  for (const c of card.cells)
    if (PRIZES.includes(c.prize as Prize) && c.revealed >= B.SCRATCH_REVEAL_COUNTS) counts[c.prize as Prize] = (counts[c.prize as Prize] ?? 0) + 1;
  let total = 0;
  for (const p of PRIZES) if ((counts[p] ?? 0) >= 3) total += B.SCRATCH_PRIZES[p];
  return card.par.mul(total).floor();
}

/** A revealed Plug part (good path) counts once the card isn't voided. */
export function scratchPlugFound(card: ScratchBonus): boolean {
  return !card.voided && card.cells.some((c) => c.prize === 'plug' && c.revealed >= B.SCRATCH_REVEAL_COUNTS);
}

/** Apply a reveal report. Returns true when the card ended (voided or fully revealed). */
export function scratchReveal(card: ScratchBonus, cell: number, revealed: number): boolean {
  const c = card.cells[cell];
  if (!c || card.done) return card.done;
  c.revealed = Math.max(c.revealed, Math.min(1, Math.max(0, revealed)));
  if (c.prize === 'eye' && c.revealed >= B.SCRATCH_TRAP_VOID) {
    card.voided = true;
    card.done = true;
    card.payout = D(0);
    return true;
  }
  const allPrizes = card.cells.every((x) => x.prize === 'eye' || x.revealed >= B.SCRATCH_REVEAL_COUNTS);
  if (allPrizes) return scratchCashOut(card);
  return false;
}

export function scratchCashOut(card: ScratchBonus): boolean {
  if (card.done) return true;
  card.payout = scratchValue(card);
  card.done = true;
  return true;
}

/** Average-skill auto-resolve: reveal every prize, never the trap. */
export function scratchAuto(card: ScratchBonus): void {
  for (const c of card.cells) if (c.prize !== 'eye') c.revealed = 1;
  scratchCashOut(card);
}
